import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { chargersApi, commandsApi, tariffsApi, sessionsApi } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { useWebSocket } from '@/context/WebSocketContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { StatusBadge, OnlineIndicator } from '@/components/StatusBadge';
import { formatDate, formatPower, timeAgo } from '@/lib/utils';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import {
  ArrowLeft, Battery, BatteryCharging, Play, Square, RefreshCw,
  Power, Unlock, Zap, Clock, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Page, GlassCard, SectionHeading, EmptyState, ErrorState, PageSkeleton, Field, DetailRows,
  ConfirmDialog, glassStrong, chartTooltipStyle, chartAxisProps, errorMessage
} from '@/components/glass/surfaces';

const ChargerDetailPage = () => {
  const { can } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const { subscribe } = useWebSocket();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notFound, setNotFound] = useState(false);
  const [charger, setCharger] = useState(null);
  const [connectors, setConnectors] = useState([]);
  const [meterValues, setMeterValues] = useState([]);
  const [tariffs, setTariffs] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [remoteStartOpen, setRemoteStartOpen] = useState(false);
  const [remoteStartData, setRemoteStartData] = useState({ connector_id: '1', id_tag: '' });
  const [commandLoading, setCommandLoading] = useState(false);
  const [pending, setPending] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);
  const fetchSeq = useRef(0);

  const fetchData = useCallback(async () => {
    const seq = ++fetchSeq.current;
    try {
      const [chargerRes, connectorsRes, meterRes, tariffsRes, sessionsRes] = await Promise.all([
        chargersApi.getById(id),
        chargersApi.getConnectors(id),
        chargersApi.getMeterValues(id, { limit: 100 }),
        tariffsApi.getAll(),
        sessionsApi.getAll({ charge_point_id: id, limit: 10 })
      ]);
      if (seq !== fetchSeq.current) return;
      setCharger(chargerRes.data);
      setConnectors(connectorsRes.data || []);
      setMeterValues(meterRes.data || []);
      setTariffs(tariffsRes.data || []);
      setSessions(sessionsRes.data || []);
      setError('');
      setNotFound(false);
    } catch (fetchError) {
      if (seq !== fetchSeq.current) return;
      console.error('Failed to fetch charger details:', fetchError);
      if (fetchError.response?.status === 404) {
        setNotFound(true);
        setCharger(null);
        setError('');
      } else {
        setError(errorMessage(fetchError, 'Failed to load charger details'));
      }
    } finally {
      if (seq === fetchSeq.current) setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    setCharger(null);
    setConnectors([]);
    setMeterValues([]);
    setSessions([]);
    setNotFound(false);
    setError('');
    setLoading(true);
  }, [id]);

  useEffect(() => {
    fetchData();
    const unsubscribe = subscribe('charger-detail', (message) => {
      if (message.charge_point_id === id) fetchData();
    });
    return () => unsubscribe();
  }, [id, subscribe, fetchData, reloadToken]);

  const handleRemoteStart = async (event) => {
    event.preventDefault();
    const idTag = remoteStartData.id_tag.trim();
    if (!idTag) {
      toast.error('Enter an ID tag');
      return;
    }
    setCommandLoading(true);
    try {
      const response = await commandsApi.remoteStart({
        charge_point_id: id,
        connector_id: parseInt(remoteStartData.connector_id, 10) || 1,
        id_tag: idTag
      });
      if (response.data.status === 'Accepted') {
        toast.success('Remote start accepted');
        setRemoteStartOpen(false);
        setRemoteStartData({ connector_id: '1', id_tag: '' });
        fetchData();
      } else {
        toast.error(`Remote start rejected: ${response.data.result?.message || 'Unknown error'}`);
      }
    } catch (startError) {
      toast.error(errorMessage(startError, 'Failed to send remote start'));
    } finally {
      setCommandLoading(false);
    }
  };

  const handleRemoteStop = async (transactionId) => {
    setCommandLoading(true);
    try {
      const response = await commandsApi.remoteStop({ transaction_id: transactionId });
      if (response.data.status === 'Accepted') {
        toast.success('Remote stop accepted');
        fetchData();
        return true;
      }
      toast.error(`Remote stop rejected: ${response.data.result?.message || 'Unknown error'}`);
      return false;
    } catch (stopError) {
      toast.error(errorMessage(stopError, 'Failed to send remote stop'));
      return false;
    } finally {
      setCommandLoading(false);
    }
  };

  const handleCommand = async (command, params = {}) => {
    setCommandLoading(true);
    try {
      let response;
      switch (command) {
        case 'reset':
          response = await commandsApi.reset({ charge_point_id: id, reset_type: params.type || 'Soft' });
          break;
        case 'unlock':
          response = await commandsApi.unlockConnector({ charge_point_id: id, connector_id: params.connector_id || 1 });
          break;
        case 'clearCache':
          response = await commandsApi.clearCache({ charge_point_id: id });
          break;
        default:
          return false;
      }
      if (response.data.result?.status === 'success') {
        toast.success('Command accepted');
      } else {
        toast.info(`Command sent — ${response.data.result?.payload?.status || 'pending'}`);
      }
      return true;
    } catch (commandError) {
      toast.error(errorMessage(commandError, 'Failed to send command'));
      return false;
    } finally {
      setCommandLoading(false);
    }
  };

  const handleTariffAssign = async (tariffId) => {
    setCommandLoading(true);
    try {
      await tariffsApi.assignToCharger(tariffId, id);
      toast.success('Tariff assigned');
      fetchData();
      return true;
    } catch (assignError) {
      toast.error(errorMessage(assignError, 'Failed to assign tariff'));
      return false;
    } finally {
      setCommandLoading(false);
    }
  };

  const ask = (config) => {
    if (commandLoading) return;
    setPending(config);
  };

  const runPending = async () => {
    if (!pending?.run) return;
    const ok = await pending.run();
    if (ok) setPending(null);
  };

  const chartData = meterValues
    .filter((item) => item.measurand === 'Power.Active.Import')
    .slice(0, 50)
    .reverse()
    .map((item) => ({
      time: new Date(item.timestamp).toLocaleTimeString(),
      power: parseFloat(item.value)
    }))
    .filter((point) => Number.isFinite(point.power));

  if ((charger && charger.charge_point_id !== id) || (loading && !charger)) {
    return <PageSkeleton testId="charger-detail-loading" />;
  }

  if (notFound) {
    return (
      <Page testId="charger-not-found">
        <ErrorState
          title="Charger not found"
          message="This charge point is not in the network, or you do not have access to it."
          onRetry={() => navigate('/chargers')}
          retryLabel="Back to chargers"
        />
      </Page>
    );
  }

  if (!charger && error) {
    return (
      <Page testId="charger-detail-page">
        <ErrorState message={error} onRetry={() => { setLoading(true); setReloadToken((value) => value + 1); }} />
      </Page>
    );
  }

  const activeSessions = sessions.filter((session) => session.is_active);
  const canOperate = can('operations.manage');
  const canPrice = can('tariffs.manage');
  const commandsDisabled = commandLoading || !charger.is_online || !canOperate;
  const currentTariff = tariffs.find((tariff) => tariff.id === charger.tariff_plan_id);

  return (
    <Page testId="charger-detail-page">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <Button type="button" variant="ghost" size="icon" className="mt-1 shrink-0 rounded-full" onClick={() => navigate('/chargers')} data-testid="back-btn" aria-label="Back to chargers">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="break-all text-3xl font-semibold tracking-tight">{charger.charge_point_id}</h1>
              <OnlineIndicator isOnline={charger.is_online} />
            </div>
            <p className="mt-1 text-sm text-muted-foreground sm:text-base">{charger.vendor || 'Unknown vendor'} {charger.model || ''}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            className="rounded-full"
            disabled={!charger.is_online || !canOperate}
            title={!canOperate ? 'You cannot send commands' : !charger.is_online ? 'Charger is offline' : 'Start a session remotely'}
            onClick={() => setRemoteStartOpen(true)}
            data-testid="remote-start-btn"
          >
            <Play className="h-4 w-4" />
            Remote Start
          </Button>
          <Button type="button" variant="outline" size="icon" className="rounded-full" onClick={() => setReloadToken((value) => value + 1)} data-testid="refresh-btn" aria-label="Refresh charger">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {error ? <ErrorState title="Could not refresh this charger" message={error} onRetry={() => setReloadToken((value) => value + 1)} /> : null}

      {!charger.is_online ? (
        <GlassCard tone="subtle" className="flex items-start gap-3 p-4">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600 dark:text-amber-300" />
          <p className="text-sm text-muted-foreground">This charger is offline. Commands stay disabled until it sends a heartbeat.</p>
        </GlassCard>
      ) : null}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <GlassCard className="p-5 sm:p-6">
            <SectionHeading title="Charger information" />
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {[
                ['Serial number', charger.serial_number || '-'],
                ['Firmware', charger.firmware_version || '-'],
                ['Site', charger.site_id || '-'],
                ['Last heartbeat', timeAgo(charger.last_heartbeat)],
                ['Registered', formatDate(charger.registered_at)],
                ['Tariff', currentTariff?.name || 'Not assigned']
              ].map(([label, value]) => (
                <div key={label} className="rounded-2xl bg-black/[0.03] px-4 py-3 dark:bg-white/[0.04]">
                  <p className="text-xs text-muted-foreground">{label}</p>
                  <p className="mt-1 break-words font-medium">{value}</p>
                </div>
              ))}
            </div>
          </GlassCard>

          <GlassCard className="p-5 sm:p-6">
            <SectionHeading icon={Battery} title="Connectors" description={connectors.length ? `${connectors.length} reported` : 'Waiting for status notifications'} />
            {connectors.length === 0 ? (
              <EmptyState compact icon={Battery} title="No connector data" description="Connectors appear after the charger sends a status notification." />
            ) : (
              <div className="grid gap-3">
                {connectors.map((connector) => (
                  <div
                    key={connector.connector_id}
                    className="flex flex-col gap-3 rounded-2xl bg-black/[0.03] p-4 sm:flex-row sm:items-center sm:justify-between dark:bg-white/[0.04]"
                    data-testid={`connector-${connector.connector_id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-foreground/5 text-lg font-semibold">
                        {connector.connector_id}
                      </div>
                      <div>
                        <StatusBadge status={connector.status} />
                        {connector.error_code && connector.error_code !== 'NoError' ? (
                          <p className="mt-1 text-sm text-destructive">{connector.error_code}</p>
                        ) : null}
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="rounded-full"
                      onClick={() => ask({
                        title: `Unlock connector ${connector.connector_id}?`,
                        description: 'The latch will release. Use this when the vehicle is ready to unplug.',
                        confirmLabel: 'Unlock connector',
                        run: () => handleCommand('unlock', { connector_id: connector.connector_id })
                      })}
                      disabled={commandsDisabled}
                      data-testid={`unlock-connector-${connector.connector_id}`}
                    >
                      <Unlock className="h-4 w-4" />
                      Unlock
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </GlassCard>

          <GlassCard className="min-w-0 p-5 sm:p-6">
            <SectionHeading icon={Zap} title="Live power" description="Latest power samples from this charger." />
            {chartData.length === 0 ? (
              <div className="flex h-[240px] flex-col items-center justify-center text-center">
                <Zap className="mb-2 h-6 w-6 text-muted-foreground" />
                <p className="text-sm font-medium">No meter data</p>
                <p className="mt-1 text-xs text-muted-foreground">Power samples appear while a session is charging.</p>
              </div>
            ) : (
              <div className="h-[240px] min-w-0">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="4 8" vertical={false} />
                    <XAxis dataKey="time" {...chartAxisProps} />
                    <YAxis {...chartAxisProps} width={40} />
                    <Tooltip contentStyle={chartTooltipStyle} formatter={(value) => [formatPower(value), 'Power']} />
                    <Line type="monotone" dataKey="power" stroke={`hsl(var(--chart-2))`} strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </GlassCard>
        </div>

        <div className="space-y-4">
          {activeSessions.map((session) => (
            <GlassCard key={session.id} className="border-sky-500/30 bg-sky-500/10 p-5 sm:p-6" data-testid="active-session-card">
              <SectionHeading icon={BatteryCharging} title="Active session" description={`Transaction #${session.transaction_id}`} />
              <DetailRows
                rows={[
                  { label: 'ID tag', value: <span className="font-mono">{session.id_tag}</span> },
                  { label: 'Started', value: formatDate(session.start_timestamp) },
                  { label: 'Energy', value: session.energy_kwh != null ? `${Number(session.energy_kwh).toFixed(2)} kWh` : '-' }
                ]}
              />
              <Button
                type="button"
                variant="destructive"
                className="mt-4 w-full rounded-full"
                onClick={() => ask({
                  title: `Stop transaction #${session.transaction_id}?`,
                  description: 'This sends a remote stop and ends the charging session on the charger.',
                  confirmLabel: 'Stop session',
                  destructive: true,
                  testId: 'confirm-remote-stop',
                  run: () => handleRemoteStop(session.transaction_id)
                })}
                disabled={commandLoading || !canOperate}
                data-testid="remote-stop-btn"
              >
                <Square className="h-4 w-4" />
                Remote Stop
              </Button>
            </GlassCard>
          ))}

          <GlassCard className="p-5 sm:p-6">
            <SectionHeading title="Commands" description="Each command asks for confirmation before it is sent." />
            {!canOperate ? (
              <p className="mb-3 text-sm text-muted-foreground">Your role can view this charger, but cannot send commands.</p>
            ) : null}
            <div className="space-y-2">
              <Button
                type="button"
                variant="outline"
                className="w-full justify-start rounded-2xl"
                onClick={() => ask({
                  title: 'Soft reset this charger?',
                  description: 'The charger restarts its software. An active session may be interrupted.',
                  confirmLabel: 'Soft reset',
                  run: () => handleCommand('reset', { type: 'Soft' })
                })}
                disabled={commandsDisabled}
                data-testid="soft-reset-btn"
              >
                <RefreshCw className="h-4 w-4" />
                Soft Reset
              </Button>
              <Button
                type="button"
                variant="outline"
                className="w-full justify-start rounded-2xl text-destructive hover:text-destructive"
                onClick={() => ask({
                  title: 'Hard reset this charger?',
                  description: 'A hard reset reboots the charger immediately and interrupts any active session.',
                  confirmLabel: 'Hard reset',
                  destructive: true,
                  run: () => handleCommand('reset', { type: 'Hard' })
                })}
                disabled={commandsDisabled}
                data-testid="hard-reset-btn"
              >
                <Power className="h-4 w-4" />
                Hard Reset
              </Button>
              <Button
                type="button"
                variant="outline"
                className="w-full justify-start rounded-2xl"
                onClick={() => ask({
                  title: 'Clear the authorization cache?',
                  description: 'The charger forgets cached ID tags and must authorize them again.',
                  confirmLabel: 'Clear cache',
                  run: () => handleCommand('clearCache')
                })}
                disabled={commandsDisabled}
                data-testid="clear-cache-btn"
              >
                <AlertTriangle className="h-4 w-4" />
                Clear Cache
              </Button>
            </div>
          </GlassCard>

          <GlassCard className="p-5 sm:p-6">
            <SectionHeading title="Tariff plan" description={canPrice ? 'Applies to new sessions on this charger.' : 'You can view the plan, but not change it.'} />
            {tariffs.length === 0 ? (
              <p className="text-sm text-muted-foreground">No tariff plans are available.</p>
            ) : (
              <Select
                value={charger.tariff_plan_id || undefined}
                onValueChange={(value) => {
                  if (value === charger.tariff_plan_id) return;
                  const plan = tariffs.find((tariff) => tariff.id === value);
                  ask({
                    title: 'Change tariff plan?',
                    description: `New sessions on this charger will use ${plan?.name || 'the selected plan'}.`,
                    confirmLabel: 'Assign tariff',
                    run: () => handleTariffAssign(value)
                  });
                }}
                disabled={!canPrice || commandLoading}
              >
                <SelectTrigger className="rounded-xl bg-background/70" data-testid="tariff-select">
                  <SelectValue placeholder="Select a tariff plan" />
                </SelectTrigger>
                <SelectContent>
                  {tariffs.map((tariff) => (
                    <SelectItem key={tariff.id} value={tariff.id}>{tariff.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </GlassCard>

          <GlassCard className="p-5 sm:p-6">
            <SectionHeading icon={Clock} title="Recent sessions" />
            {sessions.length === 0 ? (
              <EmptyState compact icon={Clock} title="No sessions yet" />
            ) : (
              <div className="space-y-2">
                {sessions.slice(0, 5).map((session) => (
                  <button
                    key={session.id}
                    type="button"
                    className="flex w-full items-center justify-between gap-3 rounded-2xl bg-black/[0.03] px-3 py-3 text-left transition-colors hover:bg-black/[0.06] dark:bg-white/[0.04] dark:hover:bg-white/[0.08]"
                    onClick={() => navigate(`/sessions/${session.id}`)}
                    data-testid={`session-${session.transaction_id}`}
                  >
                    <div className="min-w-0">
                      <p className="font-mono text-sm">#{session.transaction_id}</p>
                      <p className="text-xs text-muted-foreground">{formatDate(session.start_timestamp)}</p>
                    </div>
                    <div className="text-right">
                      <StatusBadge status={session.is_active ? 'Charging' : 'Finished'} variant="dot" />
                      {session.energy_kwh != null ? (
                        <p className="text-xs font-mono text-muted-foreground">{Number(session.energy_kwh).toFixed(2)} kWh</p>
                      ) : null}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </GlassCard>
        </div>
      </div>

      <Dialog open={remoteStartOpen} onOpenChange={(open) => { if (!commandLoading) setRemoteStartOpen(open); }}>
        <DialogContent className={`${glassStrong} max-w-md`}>
          <DialogHeader>
            <DialogTitle>Remote start</DialogTitle>
            <DialogDescription>Start a charging session on {charger.charge_point_id}.</DialogDescription>
          </DialogHeader>
          <form className="space-y-4" onSubmit={handleRemoteStart}>
            <Field label="Connector">
              <Select
                value={String(remoteStartData.connector_id)}
                onValueChange={(value) => setRemoteStartData({ ...remoteStartData, connector_id: value })}
              >
                <SelectTrigger className="rounded-xl" data-testid="connector-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(connectors.length ? connectors : [{ connector_id: 1, status: 'Unknown' }]).map((connector) => (
                    <SelectItem key={connector.connector_id} value={String(connector.connector_id)}>
                      Connector {connector.connector_id}{connector.status ? ` · ${connector.status}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="ID tag" hint="RFID card or other authorization id.">
              <Input
                placeholder="DEMO123"
                value={remoteStartData.id_tag}
                onChange={(event) => setRemoteStartData({ ...remoteStartData, id_tag: event.target.value })}
                className="rounded-xl font-mono"
                data-testid="id-tag-input"
              />
            </Field>
            <DialogFooter>
              <Button type="button" variant="outline" className="rounded-full" onClick={() => setRemoteStartOpen(false)} disabled={commandLoading}>
                Cancel
              </Button>
              <Button type="submit" className="rounded-full" disabled={commandLoading || !canOperate} data-testid="confirm-remote-start">
                {commandLoading ? 'Sending...' : 'Start session'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!pending}
        title={pending?.title || ''}
        description={pending?.description || ''}
        confirmLabel={pending?.confirmLabel}
        destructive={pending?.destructive}
        loading={commandLoading}
        onOpenChange={(open) => { if (!open) setPending(null); }}
        onConfirm={runPending}
        testId={pending?.testId || 'confirm-charger-action'}
      />
    </Page>
  );
};

export default ChargerDetailPage;
